import { fetcher } from "@/lib/api";
import { getApiBaseUrl } from "@/lib/api-base-url";
import { LEARN_REVALIDATE_SECONDS, type LearnAccessLevel } from "@/features/learn/api";

/**
 * The Learn reader's reads and its one write (`LN-RDR01`, docs/35 §5).
 *
 * Two callers, on purpose, and the split is the same one `LN-HUB01` made for
 * the hub:
 *
 * - `fetchLearnLesson` runs on the server with **no credentials** and is cached
 *   by the Next data cache for 60 s. What it returns is the half of the route
 *   that is the same for everyone: a `free` lesson's body, and a `core` or `pro`
 *   lesson's teaser. That is what makes the page a real ISR page, and it is what
 *   a search engine and a signed-out reader get with the first paint.
 * - `fetchLearnLessonForViewer` runs in the browser **with** the session and
 *   answers the same shape. A `core` lesson's body arrives here and never in the
 *   cached HTML, because caching one learner's gated body for the next visitor
 *   is the defect that split exists to prevent.
 *
 * Nothing here decides access. The server does, once, and both calls carry its
 * answer in `lesson.access`.
 */

export type LearnLessonAccessState = "granted" | "sign_in" | "pro";

export type LearnLessonRef = {
  moduleKey: string;
  lessonKey: string;
  title_en: string;
  title_bn: string | null;
  learnAccess: LearnAccessLevel;
};

export type LearnCheckpointOption = {
  id: string;
  text_en: string;
  text_bn: string | null;
  isCorrect: boolean;
  explanation_en: string | null;
  explanation_bn: string | null;
};

export type LearnCheckpoint = {
  id: string;
  questionText_en: string;
  questionText_bn: string | null;
  type: "single_choice" | "multiple_choice" | "true_false";
  options: LearnCheckpointOption[];
};

export type LearnLessonTeaser = {
  firstParagraph_en: string;
  firstParagraph_bn: string;
  outline_en: string[];
  outline_bn: string[];
};

export type LearnProblemSolution = {
  key: string;
  languageKey: string;
  solutionCode: string;
  /** `LN-PRB01`: graded by the judge through the three problem routes below. */
  judged: boolean;
  /**
   * `LN-ACC01`: a Pro problem (`pro`), and one this reader cannot reach
   * (`locked`): its `solutionCode` is `""`, its solution rung is cut from the
   * body on the server, and the judge refuses it. Optional, so a payload from a
   * server before `LN-ACC01` reads as open.
   */
  pro?: boolean;
  locked?: boolean;
};

/** `LN-PRB01`: this learner's state on one judged problem, from the lesson read. */
export type LearnProblemViewerState = {
  key: string;
  accepted: boolean;
  attempts: number;
  latest: { status: string; verdict: string | null } | null;
};

/** The judge's verdict vocabulary (docs/27 §7.1), as the wire spells it. */
export type LearnJudgeVerdict =
  | "accepted"
  | "wrong_answer"
  | "time_limit_exceeded"
  | "memory_limit_exceeded"
  | "runtime_error"
  | "compilation_error"
  | "presentation_error"
  | "internal_error"
  | "skipped";

/** What the judge tells a learner about one attempt (`shapeSubmissionForStudent`, `sample_only`). */
export type LearnProblemResult = {
  status: "queued" | "running" | "completed";
  verdict?: LearnJudgeVerdict;
  score: number;
  maxScore: number;
  compileOutput?: string;
  hidden?: { passedCount: number; totalCount: number };
};

export type LearnProblemSubmission = {
  submissionId: string;
  languageKey: string;
  languageVersion: string;
  createdAt: string;
  passed: boolean;
  settled: boolean;
  result: LearnProblemResult;
  /** On the by-id read only: rows ahead while queued. */
  queuePosition?: number | null;
};

export type LearnReaderLesson = {
  id: string;
  moduleKey: string;
  lessonKey: string;
  title_en: string;
  title_bn: string | null;
  type: string;
  learnAccess: LearnAccessLevel;
  minutes: number | null;
  module: { id: string; moduleKey: string; title_en: string; title_bn: string | null };
  access: LearnLessonAccessState;
  content_en: string | null;
  content_bn: string | null;
  teaser: LearnLessonTeaser | null;
  checkpoints: LearnCheckpoint[];
  problems: LearnProblemSolution[];
  moduleTest: LearnLessonRef | null;
  /** This lesson's own module test, when it is one (`LN-RDR02`). */
  test?: LearnTestSummary | null;
  /**
   * `LN-ASG02`: a project lesson's published assignment, when its workspace
   * opens for this reader; absent or `null` otherwise. `content_*` is the brief.
   */
  project?: { assignmentId: string } | null;
};

/** What a module test lesson says before it starts (`LN-RDR02`). */
export type LearnTestSummary = {
  questionCount: number;
  passPercentage: number;
  allowRetry: boolean;
  maxAttempts: number | null;
  timeLimitMinutes: number | null;
};

export type LearnTestQuestion = {
  _id: string;
  questionText_en: string;
  questionText_bn?: string;
  type: "single_choice" | "multiple_choice" | "true_false";
  options: Array<{ _id: string; text_en: string; text_bn?: string }>;
  marks: number;
};

export type LearnTestStart = {
  attemptId: string;
  questions: LearnTestQuestion[];
  totalMarks: number;
  attemptNumber: number;
};

export type LearnTestResult = {
  attemptId: string;
  scoredMarks: number;
  totalMarks: number;
  percentage: number;
  passed: boolean;
  completed: boolean;
  answers: Array<{
    questionId: string;
    selectedOptionIds: string[];
    isCorrect: boolean;
    correctOptionIds?: string[];
    explanation_en?: string;
    explanation_bn?: string;
  }>;
};

export type LearnTestAttempt = {
  attemptId: string;
  attemptNumber: number;
  status: string;
  percentage: number;
  passed: boolean;
  submittedAt?: string;
};

export type LearnReaderPayload = {
  track: {
    courseId: string;
    courseSlug: string;
    trackSlug: string;
    title_en: string;
    title_bn: string | null;
  };
  lesson: LearnReaderLesson;
  nav: { prev: LearnLessonRef | null; next: LearnLessonRef | null };
  viewer: {
    completed: boolean;
    openedSteps: string[];
    /** `LN-PRB01`; absent on a payload cached before the field existed. */
    problems?: LearnProblemViewerState[];
  } | null;
};

/** The reader's own address, which is also what the syllabus links at. */
export function learnLessonHref(
  locale: string,
  trackSlug: string,
  ref: Pick<LearnLessonRef, "moduleKey" | "lessonKey">,
): string {
  return `/${locale}/learn/${trackSlug}/${ref.moduleKey}/${ref.lessonKey}`;
}

function lessonPath(trackSlug: string, moduleKey: string, lessonKey: string): string {
  return `/v1/learn/tracks/${encodeURIComponent(trackSlug)}/lessons/${encodeURIComponent(
    moduleKey,
  )}/${encodeURIComponent(lessonKey)}`;
}

/**
 * The anonymous payload, server-side.
 *
 * `null` for a 404 and for an API blip alike, and the page turns that into
 * `notFound()`: a lesson page that 500s because the API hiccuped is worse than
 * one that says the lesson is not there.
 *
 * Cached by default, for the lesson page, which is prebuilt since 2026-10-20:
 * a regeneration re-reads a stale entry before it renders, so a lesson taken
 * down (or its whole track) reaches the page's `notFound()`. `live` is for the
 * lesson's share image, which renders per request. There Next's fetch cache,
 * which keeps 200s only, serves a withdrawn lesson's last 200 for good because
 * the refresh's 404 is thrown away, which is how the page kept a taken-down
 * lesson's full body while it too rendered per request.
 */
export async function fetchLearnLesson(
  trackSlug: string,
  moduleKey: string,
  lessonKey: string,
  opts: { live?: boolean } = {},
): Promise<LearnReaderPayload | null> {
  try {
    const res = await fetch(
      `${getApiBaseUrl()}${lessonPath(trackSlug, moduleKey, lessonKey)}`,
      opts.live ? { cache: "no-store" } : { next: { revalidate: LEARN_REVALIDATE_SECONDS } },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as { success?: boolean; data?: LearnReaderPayload };
    if (!json.success || !json.data?.lesson) return null;
    return json.data;
  } catch {
    return null;
  }
}

/**
 * The query key of the reader's own payload. One place, because the reader
 * reads it and its lesson links prefetch it (`LN-RDR03`), and two spellings
 * would mean a prefetch nobody ever reads.
 */
export function learnLessonViewerKey(trackSlug: string, moduleKey: string, lessonKey: string) {
  return ["learn", "lesson", trackSlug, moduleKey, lessonKey, "viewer"] as const;
}

/** The same shape read from the browser with the session. */
export async function fetchLearnLessonForViewer(
  trackSlug: string,
  moduleKey: string,
  lessonKey: string,
): Promise<LearnReaderPayload | null> {
  const res = await fetcher<LearnReaderPayload>(lessonPath(trackSlug, moduleKey, lessonKey));
  return res.success ? res.data : null;
}

/**
 * Record one opened step. Idempotent on the server, so the reader may call it
 * every time a rung is opened without counting clicks itself.
 */
export async function postLearnEvent(params: {
  trackSlug: string;
  moduleKey: string;
  lessonKey: string;
  kind: "hint" | "checkpoint";
  step: string;
}): Promise<{ created: boolean; openedSteps: string[] } | null> {
  const res = await fetcher<{ created: boolean; openedSteps: string[] }>(
    `${lessonPath(params.trackSlug, params.moduleKey, params.lessonKey)}/events`,
    {
      method: "POST",
      body: JSON.stringify({ kind: params.kind, step: params.step }),
    },
  );
  return res.success ? res.data : null;
}

type LessonRefParams = { trackSlug: string; moduleKey: string; lessonKey: string };

function apiError(res: { success: boolean; error?: { code?: string; message?: string } }): Error {
  const error = new Error(res.error?.message ?? "Request failed");
  error.name = res.error?.code ?? "ERROR";
  return error;
}

/**
 * "Done, next lesson" (`LN-RDR02`). Idempotent on the server, so a second click
 * is harmless.
 */
export async function completeLearnLesson(params: LessonRefParams): Promise<void> {
  const res = await fetcher<{ lessonId: string; completedAt: string }>(
    `${lessonPath(params.trackSlug, params.moduleKey, params.lessonKey)}/complete`,
    { method: "POST", body: JSON.stringify({}) },
  );
  if (!res.success) throw apiError(res);
}

/** Start a module test: its questions, without their answers (`LN-RDR02`). */
export async function startLearnTest(params: LessonRefParams): Promise<LearnTestStart> {
  const res = await fetcher<LearnTestStart>(
    `${lessonPath(params.trackSlug, params.moduleKey, params.lessonKey)}/test/start`,
    { method: "POST", body: JSON.stringify({}) },
  );
  if (!res.success) throw apiError(res);
  return res.data;
}

/** Grade a module test; a pass also finishes the lesson (`LN-RDR02`). */
export async function submitLearnTest(
  params: LessonRefParams & {
    attemptId: string;
    answers: Array<{ questionId: string; selectedOptionIds: string[] }>;
  },
): Promise<LearnTestResult> {
  const res = await fetcher<LearnTestResult>(
    `${lessonPath(params.trackSlug, params.moduleKey, params.lessonKey)}/test/attempts/${encodeURIComponent(
      params.attemptId,
    )}/submit`,
    { method: "POST", body: JSON.stringify({ answers: params.answers }) },
  );
  if (!res.success) throw apiError(res);
  return res.data;
}

/** This learner's finished attempts, newest first (`LN-RDR02`). */
export async function fetchLearnTestAttempts(params: LessonRefParams): Promise<LearnTestAttempt[]> {
  const res = await fetcher<{ attempts: LearnTestAttempt[] }>(
    `${lessonPath(params.trackSlug, params.moduleKey, params.lessonKey)}/test/attempts`,
  );
  return res.success ? res.data.attempts : [];
}

function problemPath(params: LessonRefParams & { problemKey: string }): string {
  return `${lessonPath(params.trackSlug, params.moduleKey, params.lessonKey)}/problems/${encodeURIComponent(
    params.problemKey,
  )}`;
}

/**
 * `LN-PRB01`: one graded attempt at one judged problem. 202 with the row's id;
 * the verdict lands later and is read back with `fetchLearnProblemSubmission`.
 * The error's `name` is the API code (`COURSE_EXERCISE_PLAN_CAP`,
 * `JUDGE_ENGINE_UNAVAILABLE`, ...), which the pane turns into a sentence.
 */
export async function submitLearnProblem(
  params: LessonRefParams & {
    problemKey: string;
    languageKey: string;
    languageVersion: string;
    sourceCode: string;
  },
): Promise<{ submissionId: string; status: string; queuePosition: number }> {
  const res = await fetcher<{ submissionId: string; status: string; queuePosition: number }>(
    `${problemPath(params)}/submissions`,
    {
      method: "POST",
      body: JSON.stringify({
        languageKey: params.languageKey,
        languageVersion: params.languageVersion,
        sourceCode: params.sourceCode,
      }),
    },
  );
  if (!res.success) throw apiError(res);
  return res.data;
}

/** One own attempt, polled until `result.status` is `completed` (`LN-PRB01`). */
export async function fetchLearnProblemSubmission(
  params: LessonRefParams & { problemKey: string; submissionId: string },
): Promise<LearnProblemSubmission> {
  const res = await fetcher<LearnProblemSubmission>(
    `${problemPath(params)}/submissions/${encodeURIComponent(params.submissionId)}`,
  );
  if (!res.success) throw apiError(res);
  return res.data;
}
